<template>
  <v-card class="at">
    <!-- Mismo esquema que los diálogos de rechazo y pago: el color de la
         acción vive en una regla lateral y en el botón final. -->
    <header class="at__head">
      <div class="at__head-text">
        <p class="at__eyebrow">Entregas</p>
        <h2 class="at__title">Adjuntar comprobante</h2>
        <p class="at__scope">
          <template v-if="code">
            Consolidado <strong>{{ code }}</strong> ·
            {{ targetIds.length }} facturas
          </template>
          <template v-else-if="invoiceNumber">
            Factura <strong>{{ invoiceNumber }}</strong>
          </template>
          <template v-else>
            {{ targetIds.length }} factura(s)
          </template>
        </p>
      </div>
      <button
        type="button"
        class="at__close"
        title="Cerrar"
        @click="$emit('close-attach')"
      >
        <v-icon small>close</v-icon>
      </button>
    </header>

    <div class="at__body">
      <!-- 1. Comprobante -->
      <section class="at__section">
        <p class="at__label">
          Comprobante de recibido
          <span class="at__req">obligatorio</span>
        </p>
        <upload-btn
          multiple
          dropzone
          hint="Guía firmada, foto o PDF. Puedes adjuntar varios."
          :invalid="submitted && !hasFiles"
          @clicked="onFilePicked"
        ></upload-btn>
        <p v-if="submitted && !hasFiles" class="at__error">
          Adjunta al menos un comprobante.
        </p>
        <p v-else class="at__help">
          Se suma al expediente; no reemplaza los documentos anteriores.
        </p>
      </section>

      <!-- 2. Expediente. Un trámite puede recibir documentos en varias fechas:
           el orden cronológico es el dato, no adorno. -->
      <section class="at__section">
        <p class="at__label">
          Expediente del trámite
          <span class="at__count">{{ documents.length }}</span>
        </p>
        <p v-if="loadingDocs" class="at__help">Cargando expediente…</p>
        <p v-else-if="!documents.length" class="at__help">
          Sin documentos previos. Este será el primero.
        </p>
        <ol v-else class="at__log">
          <li v-for="doc in documents" :key="doc.id" class="at__log-item">
            <span class="at__log-date">{{ dateOf(doc.createdAt) }}</span>
            <span class="at__tag" :class="stepClass(doc.step)">
              {{ stepLabel(doc.step) }}
            </span>
            <a
              :href="fullPath(doc.filePath)"
              target="_blank"
              rel="noopener"
              class="at__log-name"
              :title="doc.originalName || doc.filePath"
            >
              {{ doc.originalName || doc.filePath }}
            </a>
            <span v-if="doc.uploadedBy" class="at__log-who">
              {{ doc.uploadedBy.firstName }} {{ doc.uploadedBy.lastName }}
            </span>
          </li>
        </ol>
      </section>
    </div>

    <footer class="at__foot">
      <button
        type="button"
        class="at__btn at__btn--ghost"
        @click="$emit('close-attach')"
      >
        Cancelar
      </button>
      <button
        type="button"
        class="at__btn at__btn--save"
        :disabled="saving"
        @click="handleSave"
      >
        <v-icon small class="at__btn-icon">attach_file</v-icon>
        {{ saving ? 'Guardando…' : 'Guardar comprobante' }}
      </button>
    </footer>
  </v-card>
</template>

<script>
import moment from 'moment-timezone'
import UploadButton from './FileUpload'
import { getFullPath } from '@/helpers/TransitHelper'

export default {
  components: {
    'upload-btn': UploadButton,
  },
  props: {
    // { invoices: id | [ids], invoiceNumber?, code? }
    item: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    form: {
      invoices: [],
      files: null,
    },
    documents: [],
    loadingDocs: false,
    // Los errores aparecen al intentar guardar, no al abrir.
    submitted: false,
    saving: false,
  }),
  computed: {
    hasFiles() {
      return Array.isArray(this.form.files) && this.form.files.length > 0
    },
    targetIds() {
      const ids = this.item.invoices
      if (Array.isArray(ids)) return ids
      return ids == null || ids === '' ? [] : [ids]
    },
    code() {
      return this.item.code || null
    },
    invoiceNumber() {
      return this.item.invoiceNumber || null
    },
  },
  created() {
    this.form = { invoices: this.targetIds, files: null }
    this.loadDocuments()
  },
  methods: {
    fullPath: getFullPath,
    onFilePicked(files) {
      this.form.files = files
    },
    dateOf(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    stepLabel(step) {
      const labels = {
        DENIED: 'Rechazo',
        PAYED: 'Pago',
        DELIVERED: 'Entrega',
        OTHER: 'Otro',
      }
      return labels[step] || step
    },
    stepClass(step) {
      return `at__tag--${String(step).toLowerCase()}`
    },
    loadDocuments() {
      if (!this.targetIds.length) return
      this.loadingDocs = true
      this.$store
        .dispatch('invoices/getDocuments', { ids: this.targetIds.join(',') })
        .then(docs => {
          this.documents = docs || []
        })
        .finally(() => {
          this.loadingDocs = false
        })
    },
    handleSave() {
      this.submitted = true
      this.$validator.validateAll().then(result => {
        if (!result || !this.hasFiles) return
        this.saving = true
        this.$store
          .dispatch('transit/deliveredAttachment', this.form)
          .then(() => {
            this.$emit('save-attach')
          })
          .finally(() => {
            this.saving = false
          })
      })
    },
  },
}
</script>

<style scoped>
.at {
  border-radius: 12px;
  overflow: hidden;
}

/* === Encabezado === */
.at__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 14px 24px;
  border-left: 4px solid #254f87;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}
.at__head-text {
  flex: 1;
  min-width: 0;
}
.at__eyebrow {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.at__title {
  margin: 2px 0 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #0f172a;
}
.at__scope {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}
.at__scope strong {
  color: #254f87;
}
.at__close {
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 4px;
  cursor: pointer;
  line-height: 1;
  transition: background 0.18s ease;
}
.at__close:hover {
  background: #f1f5f9;
}
.at__close .v-icon {
  color: #64748b !important;
}

/* === Cuerpo === */
.at__body {
  padding: 16px 24px 4px;
  max-height: 62vh;
  overflow-y: auto;
}
.at__section {
  margin-bottom: 18px;
}
.at__label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
}
.at__req {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #b45309;
}
.at__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
}
.at__error {
  margin: 6px 0 0;
  font-size: 12px;
  color: #b91c1c;
}
.at__help {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

/* === Expediente === */
.at__log {
  list-style: none;
  margin: 0;
  padding: 0 0 0 12px;
  border-left: 2px solid #e2e8f0;
  max-height: 168px;
  overflow-y: auto;
}
.at__log-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  font-size: 12px;
}
.at__log-date {
  flex: 0 0 auto;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}
.at__tag {
  flex: 0 0 auto;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.at__tag--denied {
  background: #fee2e2;
  color: #991b1b;
}
.at__tag--payed {
  background: #dbeafe;
  color: #1e40af;
}
.at__tag--delivered {
  background: #dcfce7;
  color: #166534;
}
.at__tag--other {
  background: #f1f5f9;
  color: #475569;
}
.at__log-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #254f87;
  text-decoration: none;
}
.at__log-name:hover {
  text-decoration: underline;
}
.at__log-who {
  flex: 0 0 auto;
  color: #94a3b8;
}

/* === Pie === */
.at__foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 24px 18px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
.at__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.18s ease;
}
.at__btn:focus-visible {
  outline: 2px solid #94a3b8;
  outline-offset: 2px;
}
.at__btn--ghost {
  background: #ffffff;
  border-color: #e2e8f0;
  color: #475569;
}
.at__btn--ghost:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
/* Blanco sobre #254f87 = 8.25:1, cumple WCAG AA. */
.at__btn--save {
  background: #254f87;
  border-color: #254f87;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(12, 27, 48, 0.3),
    0 4px 10px rgba(37, 79, 135, 0.25);
}
.at__btn--save .at__btn-icon {
  color: #ffffff !important;
}
.at__btn--save:hover:not(:disabled) {
  background: #1a3a63;
  border-color: #1a3a63;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(12, 27, 48, 0.35),
    0 8px 18px rgba(37, 79, 135, 0.3);
}
.at__btn--save:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: inset 0 2px 4px rgba(12, 27, 48, 0.4);
}
.at__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}
</style>
